import { Heart, ShoppingBag, Truck } from "lucide-react";
import { Link } from "react-router-dom";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import { motion } from "framer-motion";

export const ProductCard = ({ product, index = 0 }) => {
  const wishlist = useWishlist();
  const cart = useCart();
  const wishlisted = wishlist.isWishlisted(product.id);

  const onWishlistClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (wishlisted) wishlist.remove(product.id);
    else wishlist.add(product.id, product.name);
  };

  const onQuickAdd = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    // For quick-add we need an SKU. Card data does not carry variants — fetch the PDP
    // is overkill — use first variant via a lightweight detail call.
    // To keep it simple we redirect to the PDP on click.
    window.location.href = `/product/${product.slug}`;
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(index, 8) * 0.04 }}
      className="group relative bg-white border border-jow-navy/10 hover:border-jow-gold/60 transition-colors"
      data-testid={`product-card-${product.slug}`}
    >
      <Link to={`/product/${product.slug}`} className="block">
        <div className="relative aspect-[4/5] overflow-hidden bg-jow-cream">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
          {product.discount_pct > 0 && (
            <span
              className="absolute top-3 left-3 bg-jow-pink text-white text-[10px] tracking-widest uppercase px-2 py-1"
              data-testid={`discount-${product.slug}`}
            >
              -{product.discount_pct}%
            </span>
          )}
          {product.available_on_rikshaw && (
            <span
              className="absolute bottom-3 left-3 inline-flex items-center gap-1 bg-jow-navy/85 text-jow-cream text-[10px] tracking-widest uppercase px-2 py-1"
              data-testid={`rikshaw-pill-${product.slug}`}
            >
              <Truck size={11} /> On a rikshaw
            </span>
          )}
          <button
            type="button"
            onClick={onWishlistClick}
            className={`absolute top-3 right-3 inline-flex w-9 h-9 items-center justify-center bg-white/90 backdrop-blur-sm border border-jow-navy/10 hover:border-jow-pink hover:text-jow-pink transition ${
              wishlisted ? "text-jow-pink border-jow-pink" : "text-jow-navy"
            }`}
            data-testid={`wishlist-toggle-${product.slug}`}
            aria-label="Toggle wishlist"
          >
            <Heart size={15} fill={wishlisted ? "#E91E63" : "transparent"} />
          </button>
        </div>
        <div className="p-4">
          <h3 className="font-display text-base md:text-lg text-jow-navy leading-snug line-clamp-2 min-h-[2.5em]">
            {product.name}
          </h3>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-sans text-sm font-semibold text-jow-navy">
              ₹{(product.price_inr || 0).toLocaleString("en-IN")}
            </span>
            {product.mrp_inr > product.price_inr && (
              <span className="text-xs text-jow-navy/45 line-through">
                ₹{(product.mrp_inr || 0).toLocaleString("en-IN")}
              </span>
            )}
          </div>
        </div>
      </Link>
      <div className="px-4 pb-4 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          onClick={onQuickAdd}
          className="w-full inline-flex items-center justify-center gap-2 border border-jow-navy text-jow-navy text-[10px] uppercase tracking-widest py-2 hover:bg-jow-navy hover:text-jow-cream transition"
          data-testid={`quick-view-${product.slug}`}
        >
          <ShoppingBag size={12} /> View &amp; add
        </button>
      </div>
    </motion.article>
  );
};
